import { isCommittedBooking } from '@atink/domain';
import { arrangementForArtist, computePayout } from '@/domain/commissionEngine';
import { salesByBookingId, sessionTakingsFor } from '@/domain/sessionTakings';
import { CLOCKOFF_HOUR } from '@/domain/paymentTiming';
import { ALERT_BANDS } from '@/data/reportingTargets';
import { dayKey } from '@/utils/studioDay';
import type { Artist, Booking, PayoutRecord, SalePayment, StudioId } from '@/domain/types';

// ---------------------------------------------------------------------------
// What artists are owed — THE single source (W4-RFIX). The Ops "Unpaid artists"
// tab (`opsUnpaidArtists`) and the Day sheet "Still owed to artists" panel
// (`outstandingPay`) both read this, so the two screens list the same artists,
// days and amounts. Pure: it reads only what it is passed, so the Ops desk can
// feed it the ops-view data and the Day sheet the store.
//
// DECIDED (W5 mh, W7 n4): an artist is owed for a studio-day only once
//   1. ALL their committed sessions that day are `completed` (a later session
//      still to come means they are still working), AND
//   2. 30 minutes have passed since the last finish — or the 6pm clock-off,
//      whichever comes first (everyone is paid by 6pm).
// Amount = envelope − paid. A day worked with no signed payout IS owed, even
// with no payout record written. A written deferral is owed at due − paid,
// whatever its age. Keyed per studio-day, so a row survives the rollover.
// ---------------------------------------------------------------------------

export const ARREARS_GRACE_MIN = 30;
/** How far back an unrecorded worked day is looked for (written deferrals: any age). */
export const ARREARS_LOOKBACK_DAYS = 7;

/** The artist fields the derivation reads (a store / view Artist is assignable). */
export type ArrearsArtistLite = Pick<Artist, 'id' | 'displayName' | 'commissionRate'> &
  Partial<Pick<Artist, 'commissionArrangement' | 'rates'>>;

export interface ArtistArrearsArgs {
  nowIso: string;
  /** Studio scope; [] = every studio. */
  studioIds: readonly StudioId[];
  bookings: Record<string, Booking>;
  artists: Record<string, ArrearsArtistLite>;
  payoutRecords: Record<string, PayoutRecord>;
  /**
   * The sales rung up at checkout (B1): a session is owed on its sale, not its
   * quote. Omitted = no sale on file (falls back to paid, then booked price).
   */
  salePayments?: Record<string, SalePayment>;
}

export interface ArrearsDay {
  /** `unpaid-<studio>-<artist>-<dayKey>`: stable across the rollover. */
  id: string;
  studioId: StudioId;
  artistId: string;
  artistName: string;
  /** Studio-local day key (YYYY-MM-DD). */
  dayIso: string;
  /** The day's envelope (a written deferral: its amount due). */
  envelopeAud: number;
  paidAud: number;
  /** Still owed = envelope − paid (always > 0 here). */
  amountAud: number;
  /** 'deferred' = a written arrears record; 'due' = worked, not signed off. */
  status: 'deferred' | 'due';
  /** Whole studio days between the work day and now. */
  ageDays: number;
  /** Past the arrears threshold (`ALERT_BANDS.payoutArrearsDays`). */
  escalated: boolean;
}

/** Stable row id for an artist + studio-day (dayKey = YYYY-MM-DD, studio-local). */
export function arrearsDayId(studioId: StudioId, artistId: string, key: string): string {
  return `unpaid-${studioId}-${artistId}-${key}`;
}

const MIN = 60_000;
const DAY_MS = 86_400_000;

/** The 6pm clock-off of a studio day (studio-local +10:00, as payoutFlow). */
function clockoffMs(key: string): number {
  return Date.parse(`${key}T${String(CLOCKOFF_HOUR).padStart(2, '0')}:00:00+10:00`);
}

/** When a session finished: its checkout, else its scheduled end. */
function finishMs(b: Booking): number {
  return b.checkedOutAt ? Date.parse(b.checkedOutAt) : Date.parse(b.start) + b.durationMin * MIN;
}

function dayGap(fromKey: string, toKey: string): number {
  return Math.round((Date.parse(`${toKey}T12:00:00+10:00`) - Date.parse(`${fromKey}T12:00:00+10:00`)) / DAY_MS);
}

/** Every studio-day an artist is still owed as at `nowIso`, oldest first, then by name. */
export function artistArrears(args: ArtistArrearsArgs): ArrearsDay[] {
  const { nowIso, studioIds, bookings, artists, payoutRecords } = args;
  const sales = salesByBookingId(args.salePayments ?? {});
  const nowMs = Date.parse(nowIso);
  const today = dayKey(nowIso);
  const oldest = dayKey(new Date(Date.parse(`${today}T12:00:00+10:00`) - (ARREARS_LOOKBACK_DAYS - 1) * DAY_MS).toISOString());
  const inScope = (s: StudioId) => studioIds.length === 0 || studioIds.includes(s);

  const recordFor = new Map<string, PayoutRecord>();
  for (const r of Object.values(payoutRecords)) {
    recordFor.set(arrearsDayId(r.studioId, r.artistId, dayKey(r.dayIso)), r);
  }

  const row = (
    studioId: StudioId,
    artistId: string,
    key: string,
    envelopeAud: number,
    paidAud: number,
    status: ArrearsDay['status'],
  ): ArrearsDay => {
    const ageDays = dayGap(key, today);
    return {
      id: arrearsDayId(studioId, artistId, key),
      studioId,
      artistId,
      artistName: artists[artistId]?.displayName ?? artistId,
      dayIso: key,
      envelopeAud,
      paidAud,
      amountAud: envelopeAud - paidAud,
      status,
      ageDays,
      escalated: ageDays >= ALERT_BANDS.payoutArrearsDays,
    };
  };

  // Group committed sessions by studio + artist + studio day, inside the lookback.
  const groups = new Map<string, { studioId: StudioId; artistId: string; key: string; sessions: Booking[] }>();
  for (const b of Object.values(bookings)) {
    if (!inScope(b.studioId) || !isCommittedBooking(b)) continue;
    const key = dayKey(b.start);
    if (key < oldest || key > today) continue;
    const id = arrearsDayId(b.studioId, b.artistId, key);
    const g = groups.get(id) ?? { studioId: b.studioId, artistId: b.artistId, key, sessions: [] };
    g.sessions.push(b);
    groups.set(id, g);
  }

  const out = new Map<string, ArrearsDay>();
  for (const [id, g] of groups) {
    const artist = artists[g.artistId];
    if (!artist) continue;
    if (!g.sessions.every((b) => b.status === 'completed')) continue; // still working that day
    const lastFinish = Math.max(...g.sessions.map(finishMs));
    if (nowMs < Math.min(lastFinish + ARREARS_GRACE_MIN * MIN, clockoffMs(g.key))) continue;
    const envelope = computePayout(
      arrangementForArtist({ commissionRate: artist.commissionRate, commissionArrangement: artist.commissionArrangement }),
      g.sessions.map((b) => ({ bookingId: b.id, amountAud: sessionTakingsFor(b, sales, artists) })),
    ).payoutAud;
    const paid = recordFor.get(id)?.amountPaidAud ?? 0;
    if (envelope - paid <= 0) continue;
    out.set(id, row(g.studioId, g.artistId, g.key, envelope, paid, 'due'));
  }

  // Written deferrals (arrears) are owed at due − paid, whatever their age; they
  // replace the inferred row for the same day. A future-dated one is not owed yet.
  for (const r of Object.values(payoutRecords)) {
    if (r.status !== 'deferred' || !inScope(r.studioId)) continue;
    const key = dayKey(r.dayIso);
    if (key > today) continue;
    const id = arrearsDayId(r.studioId, r.artistId, key);
    if (r.amountDueAud - r.amountPaidAud <= 0) {
      out.delete(id);
      continue;
    }
    out.set(id, row(r.studioId, r.artistId, key, r.amountDueAud, r.amountPaidAud, 'deferred'));
  }

  return [...out.values()].sort((a, b) => a.dayIso.localeCompare(b.dayIso) || a.artistName.localeCompare(b.artistName));
}
